// Custom code: bulk order

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Size grid ("bulk order") for the product page.
 *
 * Each size cell is rendered server-side from `product_option_value.variant`, i.e. the variant for that
 * size combined with the currently selected values of the other options (colour). Shopify resolves this
 * for any number of variants, so it works on products with more than 250 variants, where
 * `product.variants` would be truncated.
 *
 * When the colour changes, the variant picker fetches the page with `option_values` and dispatches
 * `variant:update` with the parsed HTML. We copy every size cell from that fresh HTML. Between the
 * colour click and the response, the grid is locked so a stale variant ID can never be submitted.
 * If a cell cannot be resolved for the selected colour, it is disabled (no guessing).
 */
class BulkVariantPicker extends Component {
  /** @type {HTMLElement | null} */
  #variantPicker = null;

  connectedCallback() {
    super.connectedCallback();

    this.bulkOptionIndex = Number(this.getAttribute('data-option-index'));
    this.pricingTiers = document.querySelector('pricing-tiers');

    this.#variantPicker = this.#findVariantPicker();
    if (this.#variantPicker) {
      this.#variantPicker.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
      this.#variantPicker.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    this.setupQuantitySelectors();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#variantPicker?.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    this.#variantPicker?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #findVariantPicker() {
    const productId = this.dataset.productId;
    const pickers = [...document.querySelectorAll('variant-picker')];
    return /** @type {HTMLElement | null} */ (
      pickers.find((picker) => picker instanceof HTMLElement && picker.dataset.productId === productId) ||
        pickers[0] ||
        null
    );
  }

  setupQuantitySelectors() {
    const els = [...this.querySelectorAll('.quantity-selector')];
    for (const el of els) {
      const input = el.querySelector('input');
      if (!input) continue;
      input.addEventListener('input', this.handleInputChange.bind(this));
      input.addEventListener('change', this.handleInputChange.bind(this));
      input.addEventListener('blur', this.handleInputChange.bind(this));

      const minus = el.querySelector('.quantity-minus');
      minus?.addEventListener('click', function () {
        const prev = Number(input.value);
        input.value = String(Math.max(0, prev - 1));
        input.dispatchEvent(new Event('change'));
      });

      const plus = el.querySelector('.quantity-plus');
      plus?.addEventListener('click', function () {
        const prev = Number(input.value);
        input.value = String(prev + 1);
        input.dispatchEvent(new Event('change'));
      });
    }
  }

  handleInputChange() {
    if (!this.pricingTiers) {
      return;
    }

    const quantityEls = [...this.querySelectorAll("[name$='quantity']")].filter(
      (el) => el instanceof HTMLInputElement && !el.disabled
    );
    const totalQuantity = quantityEls.reduce((sum, el) => sum + (Number(el.value) || 0), 0);

    // The cart discounts are "from N units" thresholds with no upper limit, so the applicable discount is
    // the one of the highest tier whose start is <= the quantity. Tiers without a percentage (e.g. the
    // "get a quote" row) never cancel the discount already reached.
    let discountPercent = 0;
    let bestFrom = -1;
    for (const tier of [...this.pricingTiers.querySelectorAll('.pricing-tier')]) {
      if (!(tier instanceof HTMLElement)) continue;
      const from = Number(tier.dataset.tierQuantityFrom) || 0;
      const percent = Number(tier.dataset.tierAmount);
      if (!tier.dataset.tierAmount || !Number.isFinite(percent) || percent <= 0) continue;
      if (totalQuantity >= from && from > bestFrom) {
        bestFrom = from;
        discountPercent = percent;
      }
    }

    const priceEls = this.querySelectorAll('[data-bulk-variant-price]');
    priceEls.forEach((el) => {
      if (!(el instanceof HTMLElement) || !el.dataset.bulkVariantPrice) return;
      const basePrice = Number(el.dataset.bulkVariantPrice);
      // Same rounding as the Shopify cart: the per-unit discount is truncated to the cent.
      const discountCents = Math.floor((basePrice * discountPercent) / 100 + 1e-9);
      el.textContent = this.#formatMoney(basePrice - discountCents);
    });
  }

  /**
   * The customer picked another colour: lock the grid until the server-rendered cells arrive.
   */
  #onVariantSelected = () => {
    this.setAttribute('data-loading', '');
    this.setAttribute('aria-busy', 'true');
    for (const item of this.#items()) {
      this.#setCellEnabled(item, false);
    }
  };

  /**
   * @param {Event & { detail?: { data?: { html?: Document, productId?: string } } }} event
   */
  #onVariantUpdate = (event) => {
    const data = event.detail?.data;
    if (data?.productId && this.dataset.productId && data.productId !== this.dataset.productId) return;

    const html = data?.html;
    const fresh = html ? this.#findFreshPicker(html) : null;
    const freshItems = fresh ? /** @type {HTMLElement[]} */ ([...fresh.querySelectorAll('[data-bulk-option-value]')]) : [];
    const selectedValues = this.#selectedOptionValues();

    for (const item of this.#items()) {
      const value = item.dataset.bulkOptionValue;
      const freshItem = freshItems.find((el) => el.dataset.bulkOptionValue === value);

      if (!freshItem || !this.#matchesSelection(freshItem, selectedValues)) {
        this.#markUnresolved(item);
        continue;
      }

      this.#copyCell(item, freshItem);
    }

    const messageEl = this.querySelector('[data-bulk-variant-message]');
    const freshMessageEl = fresh?.querySelector('[data-bulk-variant-message]');
    if (messageEl) messageEl.textContent = freshMessageEl?.textContent?.trim() ?? '';

    this.removeAttribute('data-loading');
    this.removeAttribute('aria-busy');
    this.#ensureMainVariantId();
    this.handleInputChange();
  };

  /**
   * The product form needs a main `id` (sent with quantity 0). If the selected colour + hidden size
   * combination has no variant, use the first enabled cell of the grid (same colour).
   */
  #ensureMainVariantId() {
    const idInput = document.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"] form input[name="id"]`
    );
    if (!(idInput instanceof HTMLInputElement) || idInput.value) return;
    const firstEnabled = this.querySelector('[name$="]id"]:not(:disabled):not([value=""])');
    if (firstEnabled instanceof HTMLInputElement) idInput.value = firstEnabled.value;
  }

  /** @returns {HTMLElement[]} */
  #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bulk-option-value]')]);
  }

  /**
   * @param {Document} html
   * @returns {Element | null}
   */
  #findFreshPicker(html) {
    const candidates = /** @type {HTMLElement[]} */ ([...html.querySelectorAll('bulk-variant-picker')]);
    return (
      candidates.find(
        (el) => el.dataset.blockId === this.dataset.blockId && el.dataset.optionIndex === this.dataset.optionIndex
      ) ||
      candidates.find((el) => el.dataset.optionIndex === this.dataset.optionIndex) ||
      null
    );
  }

  /**
   * Values currently checked in the variant picker, indexed by option position, or null if unknown.
   * @returns {(string | null)[] | null}
   */
  #selectedOptionValues() {
    const picker = this.#variantPicker;
    if (!picker) return null;
    const groups = [...picker.querySelectorAll('fieldset, select')].filter(
      (el) => el instanceof HTMLSelectElement || el.querySelector('input[type="radio"]')
    );
    if (!groups.length) return null;
    return groups.map((group) => {
      if (group instanceof HTMLSelectElement) return group.value;
      const checked = group.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : null;
    });
  }

  /**
   * Safety net: the cell's variant must have the selected value for every option except the size.
   * @param {HTMLElement} freshItem
   * @param {(string | null)[] | null} selectedValues
   */
  #matchesSelection(freshItem, selectedValues) {
    if (!freshItem.dataset.variantId) return true; // No variant: copied as a disabled cell.
    if (!selectedValues) return true;

    let options;
    try {
      options = JSON.parse(freshItem.dataset.variantOptions || 'null');
    } catch (error) {
      return false;
    }
    if (!Array.isArray(options)) return false;
    if (selectedValues.length !== options.length) return true; // Picker layout unknown: trust the server.

    return options.every(
      (value, index) =>
        index === this.bulkOptionIndex || selectedValues[index] == null || selectedValues[index] === value
    );
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} freshItem
   */
  #copyCell(item, freshItem) {
    const variantId = freshItem.dataset.variantId || '';
    const available = freshItem.dataset.variantAvailable === 'true' && variantId !== '';

    item.dataset.variantId = variantId;
    item.dataset.variantAvailable = String(available);
    item.dataset.variantOptions = freshItem.dataset.variantOptions || '';

    const idInput = item.querySelector('[name$="id"]');
    if (idInput instanceof HTMLInputElement) idInput.value = variantId;

    const priceEl = item.querySelector('[data-bulk-variant-price]');
    const freshPriceEl = freshItem.querySelector('[data-bulk-variant-price]');
    if (priceEl instanceof HTMLElement) {
      priceEl.dataset.bulkVariantPrice =
        freshPriceEl instanceof HTMLElement ? freshPriceEl.dataset.bulkVariantPrice ?? '' : '';
      priceEl.textContent = freshPriceEl?.textContent?.trim() ?? '';
    }

    const stockEl = item.querySelector('[data-bulk-variant-stock]');
    const freshStockEl = freshItem.querySelector('[data-bulk-variant-stock]');
    if (stockEl) stockEl.textContent = freshStockEl?.textContent?.trim() ?? '';

    const indicatorEl = item.querySelector('[data-bulk-variant-message-indicator]');
    const freshIndicatorEl = freshItem.querySelector('[data-bulk-variant-message-indicator]');
    if (indicatorEl) indicatorEl.textContent = freshIndicatorEl?.textContent?.trim() ?? '';

    this.#setCellEnabled(item, available);
  }

  /**
   * The variant for this size and the selected colour is unknown: disable it, never reuse an old ID.
   * @param {HTMLElement} item
   */
  #markUnresolved(item) {
    item.dataset.variantId = '';
    item.dataset.variantAvailable = 'false';
    item.dataset.variantOptions = '';
    const idInput = item.querySelector('[name$="id"]');
    if (idInput instanceof HTMLInputElement) idInput.value = '';
    const priceEl = item.querySelector('[data-bulk-variant-price]');
    if (priceEl instanceof HTMLElement) {
      priceEl.dataset.bulkVariantPrice = '';
      priceEl.textContent = '';
    }
    const stockEl = item.querySelector('[data-bulk-variant-stock]');
    if (stockEl) stockEl.textContent = '';
    this.#setCellEnabled(item, false);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} enabled
   */
  #setCellEnabled(item, enabled) {
    const canEnable = enabled && item.dataset.variantAvailable === 'true' && !!item.dataset.variantId;
    for (const el of item.querySelectorAll('[name$="id"], [name$="quantity"], .quantity-plus, .quantity-minus')) {
      if (canEnable) {
        el.removeAttribute('disabled');
      } else {
        el.setAttribute('disabled', 'disabled');
      }
    }
  }

  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }
}

if (!customElements.get('bulk-variant-picker')) {
  customElements.define('bulk-variant-picker', BulkVariantPicker);
}


/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
